import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Briefcase, CheckCircle2, Upload, X, AlertCircle, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const HERO = "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1920&q=80";

const MAX_PHOTO_MB = 2;
const MAX_RESUME_MB = 5;
const MAX_CERT_MB = 5;
const MAX_CERTS = 5;

const INITIAL = {
  full_name: '', date_of_birth: '', gender: '', father_or_husband_name: '',
  mobile: '', alternate_mobile: '', email: '',
  current_address: '', permanent_address: '', same_as_current: false,
  city: '', state: '', pincode: '',
  category: '', department: '', designation: '', qualification: '',
  registration_number: '', years_experience: '',
  current_employer: '', current_designation: '',
  hear_about_us: '', remarks: '', declaration: false,
  website: '', // honeypot
};

function Section({ num, title, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8" data-testid={`section-${num}`}>
      <div className="flex items-center gap-3 mb-5">
        <span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center text-[#4ECDC4] text-sm font-bold">{num}</span>
        <h3 className="font-heading text-lg font-semibold text-[#1A202C]">{title}</h3>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, required, error, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      {children}
      {hint && !error && <p className="text-xs text-gray-400">{hint}</p>}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={11} />{error}</p>
      )}
    </div>
  );
}

function FileInput({ label, accept, maxMb, files, onChange, multiple, testId, required }) {
  const handle = (e) => {
    const list = Array.from(e.target.files || []);
    const filtered = list.filter(f => {
      if (f.size > maxMb * 1024 * 1024) {
        alert(`${f.name} exceeds ${maxMb}MB limit`);
        return false;
      }
      return true;
    });
    onChange(multiple ? filtered : filtered[0] || null);
  };
  const remove = (idx) => {
    if (multiple) onChange(files.filter((_, i) => i !== idx));
    else onChange(null);
  };
  const arr = multiple ? (files || []) : (files ? [files] : []);
  return (
    <Field label={label} required={required} hint={`${accept} · max ${maxMb}MB${multiple ? ` · up to ${MAX_CERTS} files` : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-dashed border-gray-300 rounded-lg cursor-pointer text-sm text-gray-700 transition-colors">
          <Upload size={14} />{arr.length ? 'Add More' : 'Choose File'}
          <input type="file" accept={accept} multiple={multiple} onChange={handle} className="hidden" data-testid={testId} />
        </label>
        {arr.length === 0 && <span className="text-xs text-gray-400">No file selected</span>}
      </div>
      {arr.length > 0 && (
        <div className="mt-2 space-y-1">
          {arr.map((f, i) => (
            <div key={i} className="flex items-center justify-between bg-[#4ECDC4]/5 px-3 py-2 rounded-lg text-xs">
              <span className="truncate text-gray-700">{f.name} · {(f.size / 1024).toFixed(0)} KB</span>
              <button type="button" onClick={() => remove(i)} className="text-red-500 hover:text-red-700 shrink-0 ml-2">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Field>
  );
}

export default function Careers() {
  const [form, setForm] = useState(INITIAL);
  const [photo, setPhoto] = useState(null);
  const [resume, setResume] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/careers/config`)
      .then(r => r.json())
      .then(setConfig)
      .catch(() => setConfigError(true));
  }, []);

  // Auto-fill permanent address when "same as current" is checked
  useEffect(() => {
    if (form.same_as_current) {
      setForm(p => ({ ...p, permanent_address: p.current_address }));
    }
  }, [form.same_as_current, form.current_address]);

  const isTeaching = form.category === 'Teaching Staff';

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Full name is required';
    if (!form.date_of_birth) e.date_of_birth = 'Date of birth is required';
    if (!form.gender) e.gender = 'Gender is required';
    if (!form.father_or_husband_name.trim()) e.father_or_husband_name = "Father's/Husband's name is required";
    if (!/^\d{10}$/.test(form.mobile)) e.mobile = 'Enter a valid 10-digit mobile number';
    if (form.alternate_mobile && !/^\d{10}$/.test(form.alternate_mobile)) e.alternate_mobile = 'Must be 10 digits';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.current_address.trim()) e.current_address = 'Current address is required';
    if (!form.city.trim()) e.city = 'City is required';
    if (!form.state) e.state = 'State is required';
    if (!/^\d{6}$/.test(form.pincode)) e.pincode = 'PIN code must be 6 digits';
    if (!form.category) e.category = 'Select a category';
    if (isTeaching && !form.department) e.department = 'Department is required for teaching roles';
    if (isTeaching && !form.designation) e.designation = 'Designation is required for teaching roles';
    if (!form.qualification) e.qualification = 'Qualification is required';
    if (form.years_experience === '' || isNaN(form.years_experience) || Number(form.years_experience) < 0)
      e.years_experience = 'Enter years of experience (0 or more)';
    if (form.remarks && form.remarks.length > 500) e.remarks = 'Remarks must be under 500 characters';
    if (!resume) e.resume = 'Please upload your resume/CV';
    if (certificates.length > MAX_CERTS) e.certificates = `Maximum ${MAX_CERTS} files`;
    if (!form.declaration) e.declaration = 'You must accept the declaration to submit';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'same_as_current') return;
      if (v === '' || v === null || v === undefined) return;
      fd.append(k, typeof v === 'boolean' ? (v ? 'true' : 'false') : String(v));
    });
    if (photo) fd.append('photo', photo);
    if (resume) fd.append('resume', resume);
    certificates.forEach(c => fd.append('certificates', c));

    try {
      const res = await fetch(`${API}/api/careers/apply`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      alert(`Submission failed: ${err.message}`);
    }
    setSubmitting(false);
  };

  const categories = useMemo(() => config?.categories || [], [config]);
  const departments = useMemo(() => config?.departments || [], [config]);
  const designations = useMemo(() => config?.designations || [], [config]);
  const qualifications = useMemo(() => config?.qualifications || [], [config]);
  const hearOptions = useMemo(() => config?.hear_about_us || [], [config]);
  const states = useMemo(() => config?.indian_states || [], [config]);

  if (submitted) {
    return (
      <div className="page-transition" data-testid="careers-success">
        <section className="py-24 bg-gradient-to-br from-[#F8F9FA] to-white min-h-[70vh] flex items-center">
          <div className="container-custom">
            <Card className="max-w-2xl mx-auto border-0 shadow-lg">
              <CardContent className="p-10 text-center">
                <div className="w-20 h-20 bg-[#4ECDC4]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="text-[#4ECDC4]" size={44} />
                </div>
                <h2 className="font-heading text-3xl font-bold text-[#1A202C] mb-3">Application Received</h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Thank you for your application. Our HR team will review your details and get back to you shortly.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/"><Button variant="outline" className="rounded-full"><ArrowLeft size={14} className="mr-1" />Back to Home</Button></Link>
                  <Button onClick={() => { setForm(INITIAL); setPhoto(null); setResume(null); setCertificates([]); setErrors({}); setSubmitted(false); }} className="bg-[#4ECDC4] hover:bg-[#3DBDB4] rounded-full" data-testid="submit-another">
                    Submit Another
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="container-custom py-24 text-center">
        <p className="text-gray-500">Could not load careers page. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="page-transition" data-testid="careers-page">
      {/* Hero */}
      <section className="relative py-20 md:py-28 hero-overlay" style={{ backgroundImage: `url(${HERO})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Briefcase size={14} />Work With Us
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-4">Join Our Team at SSMCH</h1>
            <p className="text-lg text-white/90 leading-relaxed">
              Be part of a mission-driven institution shaping the future of healthcare in Central India. Fill in the form below to apply for any role that matches your expertise.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section-padding bg-[#F8F9FA]">
        <div className="container-custom max-w-4xl">
          {Object.keys(errors).length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="form-errors">
              <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-700">Please correct the highlighted fields before submitting.</p>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-5" noValidate>
            {/* Honeypot */}
            <input type="text" name="website" value={form.website} onChange={e => set('website', e.target.value)} className="absolute opacity-0 pointer-events-none -z-10" tabIndex="-1" autoComplete="off" aria-hidden="true" />

            <Section num="1" title="Personal Information">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full Name" required error={errors.full_name}>
                  <Input value={form.full_name} onChange={e => set('full_name', e.target.value)} data-testid="field-full-name" />
                </Field>
                <Field label="Date of Birth" required error={errors.date_of_birth}>
                  <Input type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} data-testid="field-dob" />
                </Field>
                <Field label="Gender" required error={errors.gender}>
                  <select value={form.gender} onChange={e => set('gender', e.target.value)} className="w-full h-10 border rounded-md px-3 text-sm bg-white" data-testid="field-gender">
                    <option value="">Select...</option>
                    <option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </Field>
                <Field label="Father's / Husband's Name" required error={errors.father_or_husband_name}>
                  <Input value={form.father_or_husband_name} onChange={e => set('father_or_husband_name', e.target.value)} data-testid="field-guardian" />
                </Field>
              </div>
              <FileInput label="Photograph (optional)" accept=".jpg,.jpeg,.png" maxMb={MAX_PHOTO_MB} files={photo} onChange={setPhoto} testId="field-photo" />
            </Section>

            <Section num="2" title="Contact Information">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Mobile Number" required error={errors.mobile} hint="10-digit Indian mobile">
                  <Input inputMode="numeric" maxLength={10} value={form.mobile} onChange={e => set('mobile', e.target.value.replace(/\D/g, ''))} data-testid="field-mobile" />
                </Field>
                <Field label="Alternate Mobile" error={errors.alternate_mobile}>
                  <Input inputMode="numeric" maxLength={10} value={form.alternate_mobile} onChange={e => set('alternate_mobile', e.target.value.replace(/\D/g, ''))} data-testid="field-alt-mobile" />
                </Field>
                <Field label="Email Address" required error={errors.email}>
                  <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} data-testid="field-email" />
                </Field>
                <Field label="City" required error={errors.city}>
                  <Input value={form.city} onChange={e => set('city', e.target.value)} data-testid="field-city" />
                </Field>
                <Field label="State" required error={errors.state}>
                  <select value={form.state} onChange={e => set('state', e.target.value)} className="w-full h-10 border rounded-md px-3 text-sm bg-white" data-testid="field-state">
                    <option value="">Select...</option>
                    {states.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="PIN Code" required error={errors.pincode}>
                  <Input inputMode="numeric" maxLength={6} value={form.pincode} onChange={e => set('pincode', e.target.value.replace(/\D/g, ''))} data-testid="field-pincode" />
                </Field>
              </div>
              <Field label="Current Address" required error={errors.current_address}>
                <Textarea rows={2} value={form.current_address} onChange={e => set('current_address', e.target.value)} data-testid="field-current-address" />
              </Field>
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-700 mb-2 cursor-pointer">
                  <input type="checkbox" checked={form.same_as_current} onChange={e => set('same_as_current', e.target.checked)} data-testid="field-same-address" />
                  Same as current address
                </label>
                <Field label="Permanent Address">
                  <Textarea rows={2} value={form.permanent_address} onChange={e => set('permanent_address', e.target.value)} disabled={form.same_as_current} data-testid="field-permanent-address" />
                </Field>
              </div>
            </Section>

            <Section num="3" title="Professional Information">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Applying for Category" required error={errors.category}>
                  <select value={form.category} onChange={e => { set('category', e.target.value); if (e.target.value !== 'Teaching Staff') { set('department', ''); set('designation', ''); } }} className="w-full h-10 border rounded-md px-3 text-sm bg-white" data-testid="field-category">
                    <option value="">Select...</option>
                    {categories.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Highest Qualification" required error={errors.qualification}>
                  <select value={form.qualification} onChange={e => set('qualification', e.target.value)} className="w-full h-10 border rounded-md px-3 text-sm bg-white" data-testid="field-qualification">
                    <option value="">Select...</option>
                    {qualifications.map(q => <option key={q}>{q}</option>)}
                  </select>
                </Field>
                {isTeaching && (
                  <>
                    <Field label="Department" required error={errors.department}>
                      <select value={form.department} onChange={e => set('department', e.target.value)} className="w-full h-10 border rounded-md px-3 text-sm bg-white" data-testid="field-department">
                        <option value="">Select...</option>
                        {departments.map(d => <option key={d}>{d}</option>)}
                      </select>
                    </Field>
                    <Field label="Designation Applying For" required error={errors.designation}>
                      <select value={form.designation} onChange={e => set('designation', e.target.value)} className="w-full h-10 border rounded-md px-3 text-sm bg-white" data-testid="field-designation">
                        <option value="">Select...</option>
                        {designations.map(d => <option key={d}>{d}</option>)}
                      </select>
                    </Field>
                  </>
                )}
                <Field label="Registration Number" hint="For medical professionals (MCI/NMC/State)">
                  <Input value={form.registration_number} onChange={e => set('registration_number', e.target.value)} data-testid="field-reg-no" />
                </Field>
                <Field label="Total Years of Experience" required error={errors.years_experience}>
                  <Input type="number" min="0" step="0.5" value={form.years_experience} onChange={e => set('years_experience', e.target.value)} data-testid="field-experience" />
                </Field>
                <Field label="Current Employer / Institution">
                  <Input value={form.current_employer} onChange={e => set('current_employer', e.target.value)} data-testid="field-employer" />
                </Field>
                <Field label="Current Designation">
                  <Input value={form.current_designation} onChange={e => set('current_designation', e.target.value)} data-testid="field-current-designation" />
                </Field>
              </div>
            </Section>

            <Section num="4" title="Documents">
              <FileInput label="Resume / CV" required accept=".pdf,.doc,.docx" maxMb={MAX_RESUME_MB} files={resume} onChange={setResume} testId="field-resume" />
              {errors.resume && <p className="text-xs text-red-500 -mt-2">{errors.resume}</p>}
              <FileInput label="Certificates (optional)" accept=".pdf,.jpg,.jpeg,.png" maxMb={MAX_CERT_MB} files={certificates} onChange={setCertificates} multiple testId="field-certificates" />
              {errors.certificates && <p className="text-xs text-red-500 -mt-2">{errors.certificates}</p>}
            </Section>

            <Section num="5" title="Additional Information">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="How did you hear about us?">
                  <select value={form.hear_about_us} onChange={e => set('hear_about_us', e.target.value)} className="w-full h-10 border rounded-md px-3 text-sm bg-white" data-testid="field-hear-about">
                    <option value="">Select...</option>
                    {hearOptions.map(h => <option key={h}>{h}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Additional remarks" error={errors.remarks} hint={`${form.remarks.length}/500 characters`}>
                <Textarea rows={3} maxLength={500} value={form.remarks} onChange={e => set('remarks', e.target.value)} data-testid="field-remarks" />
              </Field>
            </Section>

            <Section num="6" title="Declaration">
              <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={form.declaration} onChange={e => set('declaration', e.target.checked)} className="mt-1" data-testid="field-declaration" />
                <span>
                  I hereby declare that the information provided above is true and correct to the best of my knowledge. I understand that any false information may lead to disqualification of my application.
                </span>
              </label>
              {errors.declaration && <p className="text-xs text-red-500">{errors.declaration}</p>}
            </Section>

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={submitting} className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-8 py-6 text-base font-medium shadow-lg" data-testid="submit-application-btn">
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
